import { EMULATOR_TRANSCRIPT_STATUS_OPTIONS } from "./api";

export type EmulatorSummaryEntry = {
  id: string;
  text: string;
  createdAt: string;
};

export type EmulatorSummarySettings = {
  autoSummary: boolean;
  delaySeconds: number;
};

export const SUMMARY_HISTORY_KEY = "m360.flexEmulatorSummaryHistory";
export const SUMMARY_SETTINGS_KEY = "m360.flexEmulatorSummarySettings";
export const STAGED_SUMMARY_KEY = "m360.flexEmulatorStagedSummary";
export const SUMMARY_HISTORY_LIMIT = 10;

export const DEFAULT_SUMMARY_SETTINGS: EmulatorSummarySettings = {
  autoSummary: true,
  delaySeconds: 0
};

/**
 * Realistic Membership Services call summaries. These seed the local summary
 * library on first run and double as fixtures for summary-driven feature work
 * (e.g. automatic topic classification): each one leans into a distinct call
 * type a real agent would categorize differently.
 */
export const SEED_SUMMARIES: readonly string[] = [
  "Member called to renew annual dues before the deadline. Verified the balance, processed full payment over the phone, and emailed a receipt. Member asked about installment options for next year; explained the billing schedule. No follow-up needed.",
  "Member could not post weekend scores to their GHIN handicap. Found an expired club affiliation blocking the sync, walked them through re-linking the club in the app, and confirmed the scores posted. Asked them to call back if the next round fails to sync.",
  "Associate asked about entering the upcoming section championship qualifier. Confirmed eligibility, explained the entry deadline and fee, and walked through the online registration steps. They plan to register tonight; no further action unless payment fails.",
  "Member is short PDR credits for the current cycle and asked what counts. Reviewed their transcript, identified two attended seminars missing paperwork, and explained how to submit credits through the education portal. Sent the submission link by email.",
  "Parent of a 12-year-old asked about PGA Jr. League 13U registration. Explained the season timeline, found two nearby facilities with open teams, and shared the league director's contact. They will register this week.",
  "Associate looking for a first assistant professional role asked about career services. Reviewed certification requirements for the target position, referred them to the section career consultant for a resume review, and shared the job board link. Consultant will reach out within two business days.",
  "Member asked for a ruling on free relief from casual water on the 7th hole during a weekend round. Explained the relief procedure and nearest point of complete relief, and pointed to the relevant rule in the app. Friendly debate resolved; no formal ruling requested.",
  "Member relocated to a new state and wants to transfer section affiliation. Confirmed good standing, explained the transfer request process and timing, and submitted the request on their behalf. New section office will confirm within a week.",
  "Veteran member asked about PGA HOPE programming for disabled veterans. Explained the program structure and current clinic schedule, confirmed eligibility basics, and connected them with the local program coordinator. Coordinator following up directly.",
  "Member was locked out of their online account and needed to update their email address. Verified identity, updated the contact email, and walked through the password reset. Confirmed login works. Asked them to verify the dues receipt arrives at the new address."
];

const generateEntryId = (): string => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

export const toHistoryEntries = (texts: readonly string[]): EmulatorSummaryEntry[] =>
  texts.map((text) => ({ id: generateEntryId(), text, createdAt: new Date().toISOString() }));

const readStorageItem = (key: string): string | null => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeStorageItem = (key: string, value: string): void => {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Storage unavailable (private mode, SSR); the library lasts for this page only.
  }
};

const isHistoryEntry = (value: unknown): value is EmulatorSummaryEntry => {
  if (!value || typeof value !== "object") return false;
  const entry = value as Partial<EmulatorSummaryEntry>;
  return typeof entry.id === "string" && typeof entry.text === "string";
};

/**
 * Most-recent-first summary history (capped at 10). Seeds from realistic
 * Membership Services calls on first run; an explicitly emptied library stays
 * empty until restored.
 */
export const loadSummaryHistory = (): EmulatorSummaryEntry[] => {
  const rawValue = readStorageItem(SUMMARY_HISTORY_KEY);
  if (rawValue === null) {
    const seeded = toHistoryEntries(SEED_SUMMARIES);
    writeStorageItem(SUMMARY_HISTORY_KEY, JSON.stringify(seeded));
    return seeded;
  }

  try {
    const parsed: unknown = JSON.parse(rawValue);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isHistoryEntry).slice(0, SUMMARY_HISTORY_LIMIT);
  } catch {
    return [];
  }
};

export const pushSummaryHistory = (text: string): EmulatorSummaryEntry[] => {
  const trimmed = text.trim();
  const current = loadSummaryHistory().filter((entry) => entry.text !== trimmed);
  const next = trimmed
    ? [{ id: generateEntryId(), text: trimmed, createdAt: new Date().toISOString() }, ...current].slice(
        0,
        SUMMARY_HISTORY_LIMIT
      )
    : current;
  writeStorageItem(SUMMARY_HISTORY_KEY, JSON.stringify(next));
  return next;
};

export const removeSummaryHistoryEntry = (id: string): EmulatorSummaryEntry[] => {
  const next = loadSummaryHistory().filter((entry) => entry.id !== id);
  writeStorageItem(SUMMARY_HISTORY_KEY, JSON.stringify(next));
  return next;
};

export const restoreSeedSummaries = (): EmulatorSummaryEntry[] => {
  const seeded = toHistoryEntries(SEED_SUMMARIES);
  writeStorageItem(SUMMARY_HISTORY_KEY, JSON.stringify(seeded));
  return seeded;
};

/** Moves an entry to the top (most-recently-used first) and returns the list. */
export const promoteSummaryEntry = (id: string): EmulatorSummaryEntry[] => {
  const current = loadSummaryHistory();
  const entry = current.find((item) => item.id === id);
  if (!entry) return current;
  const next = [entry, ...current.filter((item) => item.id !== id)];
  writeStorageItem(SUMMARY_HISTORY_KEY, JSON.stringify(next));
  return next;
};

export type StagedSummary = {
  text: string;
  status: string;
};

export const DEFAULT_STAGED_SUMMARY: StagedSummary = { text: "", status: "completed" };

const EMULATOR_TRANSCRIPT_STATUSES: readonly string[] = EMULATOR_TRANSCRIPT_STATUS_OPTIONS.map(
  (option) => option.value
);

/**
 * The summary + outcome currently staged to send. Persisted so a reload,
 * remount, or hot-update between picking and sending never loses it.
 */
export const loadStagedSummary = (): StagedSummary => {
  const rawValue = readStorageItem(STAGED_SUMMARY_KEY);
  if (!rawValue) return { ...DEFAULT_STAGED_SUMMARY };

  try {
    const parsed = JSON.parse(rawValue) as Partial<StagedSummary>;
    const status = typeof parsed.status === "string" ? parsed.status : "";
    return {
      text: typeof parsed.text === "string" ? parsed.text : "",
      status: (EMULATOR_TRANSCRIPT_STATUSES as readonly string[]).includes(status)
        ? status
        : DEFAULT_STAGED_SUMMARY.status
    };
  } catch {
    return { ...DEFAULT_STAGED_SUMMARY };
  }
};

export const saveStagedSummary = (staged: StagedSummary): void => {
  writeStorageItem(STAGED_SUMMARY_KEY, JSON.stringify(staged));
};

export const loadSummarySettings = (): EmulatorSummarySettings => {
  const rawValue = readStorageItem(SUMMARY_SETTINGS_KEY);
  if (!rawValue) return { ...DEFAULT_SUMMARY_SETTINGS };

  try {
    const parsed = JSON.parse(rawValue) as Partial<EmulatorSummarySettings>;
    return {
      autoSummary:
        parsed.autoSummary === undefined ? DEFAULT_SUMMARY_SETTINGS.autoSummary : Boolean(parsed.autoSummary),
      delaySeconds: Math.max(0, Number.parseInt(String(parsed.delaySeconds ?? 0), 10) || 0)
    };
  } catch {
    return { ...DEFAULT_SUMMARY_SETTINGS };
  }
};

export const saveSummarySettings = (settings: EmulatorSummarySettings): void => {
  writeStorageItem(SUMMARY_SETTINGS_KEY, JSON.stringify(settings));
};
